// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Delivery addresses  (used by the onboarding step /welcome/delivery and by the address
// book /account/addresses for adding and editing)
// The whole "where do we deliver?" form: a tabbed pin-on-map / search-a-place picker, GPS, the
// address fields (prefilled from the pin, always editable), tag, courier requests and a sticky
// save bar. Creates a new address, or edits `initial`. Pages own the header and what happens next.
// Data: utils/addresses.ts (API) and utils/geo.ts (OpenStreetMap search, reverse lookup, zones).
// ─────────────────────────────────────────────────────────────────────────────
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Briefcase, CircleAlert, CircleCheck, Crosshair, Home, Loader2, MapPin, Search, Snowflake, Users, X } from "lucide-react";
import type { Pin } from "./PinMap";
import { ApiError } from "../../utils/api";
import { addAddress, updateAddress, type Address, type AddressInput, type AddressLabel } from "../../utils/addresses";
import { findServiceZone, reversePlace, searchPlaces, type Place } from "../../utils/geo";
import useZones from "../../hooks/useZones";
import { findActiveZone } from "../../utils/zones";

// Leaflet is large: only loaded when a map is actually shown.
const PinMap = lazy(() => import("./PinMap"));

const INPUT =
  "h-12 w-full rounded-lg bg-ocean-700/5 px-4 text-base text-ocean-950 outline-none placeholder:text-ocean-800/50 focus:bg-ocean-700/10 focus:ring-2 focus:ring-teal-600 dark:bg-white/10 dark:text-foam-50 dark:placeholder:text-foam-100/50";

const TAGS: { value: AddressLabel; label: string; icon: typeof Home }[] = [
  { value: "home", label: "Home", icon: Home },
  { value: "work", label: "Work", icon: Briefcase },
  { value: "other", label: "Friends & family", icon: Users },
];

const INSTRUCTIONS = [
  { key: "leaveWithSecurity", title: "Leave in an insulated box with security", body: "The courier may leave the order with your building security." },
  { key: "directHandover", title: "Hand over to me directly", body: "Ring the bell so you can check the pack yourself." },
  { key: "whatsappBeforeArrival", title: "Message me on WhatsApp before arrival", body: "So you can have the kitchen counter or fridge ready." },
] as const;

type InstructionKey = (typeof INSTRUCTIONS)[number]["key"];
type GeoState = "idle" | "locating" | "looking-up";

interface AddressFormProps {
  /** The address being edited, or null to add a new one. */
  initial: Address | null;
  submitLabel: string;
  /** Start looking up the device location as soon as the form opens (the address book's "Use current live location"). */
  autoLocate?: boolean;
  onSaved: (address: Address) => void;
}

export default function AddressForm({ initial, submitLabel, autoLocate = false, onSaved }: AddressFormProps) {
  const [mode, setMode] = useState<"pin" | "search">("pin");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Place[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [pin, setPin] = useState<Pin | null>(initial ? { lat: initial.lat, lng: initial.lng } : null);
  const [focusKey, setFocusKey] = useState(initial ? 1 : 0);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [geoState, setGeoState] = useState<GeoState>("idle");
  const [geoMessage, setGeoMessage] = useState<string | null>(null);
  const [house, setHouse] = useState(initial?.house ?? "");
  const [street, setStreet] = useState(initial?.street ?? "");
  const [landmark, setLandmark] = useState(initial?.landmark ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [state, setState] = useState(initial?.state ?? "");
  const [pincode, setPincode] = useState(initial?.pincode ?? "");
  const [label, setLabel] = useState<AddressLabel>(initial?.label ?? "home");
  const [makeDefault, setMakeDefault] = useState(false);
  const [instructions, setInstructions] = useState<Record<InstructionKey, boolean>>({
    leaveWithSecurity: initial?.instructions.leaveWithSecurity ?? false,
    directHandover: initial?.instructions.directHandover ?? false,
    whatsappBeforeArrival: initial?.instructions.whatsappBeforeArrival ?? false,
  });
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lookupAbort = useRef<AbortController | null>(null);

  /** Fill the address fields from a reverse-geocoded place, without overwriting what was typed. */
  const applyPlace = useCallback((place: Place) => {
    setStreet((current) => current || place.street);
    setHouse((current) => current || place.house);
    setCity(place.city);
    setState(place.state);
    setPincode(place.pincode);
  }, []);

  /** Move the pin, then look up what is there. A newer move cancels an older lookup. */
  const placePin = useCallback(
    async (next: Pin, options: { fly?: boolean; known?: Place } = {}) => {
      setPin(next);
      setGeoMessage(null);
      if (options.fly) setFocusKey((key) => key + 1);
      if (options.known) {
        applyPlace(options.known);
        return;
      }
      lookupAbort.current?.abort();
      const controller = new AbortController();
      lookupAbort.current = controller;
      setGeoState("looking-up");
      try {
        const place = await reversePlace(next.lat, next.lng, controller.signal);
        if (place) applyPlace(place);
        else setGeoMessage("We couldn't find an address for that spot. Please fill it in below.");
      } catch (caught) {
        if (!(caught instanceof DOMException && caught.name === "AbortError")) {
          setGeoMessage("We couldn't look up that spot's address. Please fill it in below.");
        }
      } finally {
        if (lookupAbort.current === controller) setGeoState("idle");
      }
    },
    [applyPlace],
  );

  const locateMe = () => {
    if (!("geolocation" in navigator)) {
      setGeoMessage("Your browser can't share your location. Search for a place or tap the map instead.");
      return;
    }
    setGeoState("locating");
    setGeoMessage(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setAccuracy(Math.round(position.coords.accuracy));
        setMode("pin");
        void placePin({ lat: position.coords.latitude, lng: position.coords.longitude }, { fly: true });
      },
      (failure) => {
        setGeoState("idle");
        setGeoMessage(
          failure.code === failure.PERMISSION_DENIED
            ? "Location permission was blocked. Search for a place or tap the map instead."
            : "We couldn't get your location. Search for a place or tap the map instead.",
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // Opened via "Use current live location": ask for the position once, right away.
  useEffect(() => {
    if (autoLocate) locateMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async () => {
    if (query.trim().length < 3) return;
    setSearching(true);
    setGeoMessage(null);
    try {
      const found = await searchPlaces(query.trim());
      setResults(found);
      if (found.length === 0) setGeoMessage("No places found. Try a landmark, area or pincode.");
    } catch {
      setGeoMessage("Search isn't available right now. Tap the map to drop a pin instead.");
    } finally {
      setSearching(false);
    }
  };

  const choosePlace = (place: Place) => {
    setResults(null);
    setAccuracy(null);
    setQuery(place.label);
    void placePin({ lat: place.lat, lng: place.lng }, { fly: true, known: place });
  };

  // Once staff have drawn delivery zones (utils/zones), THEY decide what "we deliver here" means; before that, the illustrative city boxes do.
  const zones = useZones();
  const live = pin && zones.enforced ? findActiveZone(zones.zones, pin) : null;
  const zone = zones.enforced ? (live ? { zoneName: live.name, availability: "available" as const, typicalDeliveryWindow: undefined } : null) : pin ? findServiceZone(pin.lat, pin.lng) : null;
  const serviceable = zone?.availability === "available";
  const problems = {
    pin: pin === null,
    house: house.trim() === "",
    street: street.trim() === "",
    city: city.trim() === "",
    state: state.trim() === "",
    pincode: !/^[1-9][0-9]{5}$/.test(pincode),
  };
  const invalid = Object.values(problems).some(Boolean);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setTouched(true);
    if (invalid || !pin) {
      const first = problems.pin ? "delivery-pin-area" : (["house", "street", "city", "state", "pincode"] as const).find((f) => problems[f]);
      document.getElementById(first === "delivery-pin-area" ? first : `delivery-${first}`)?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    const input: AddressInput = {
      label,
      house: house.trim(),
      street: street.trim(),
      landmark: landmark.trim() || undefined,
      city: city.trim(),
      state: state.trim(),
      pincode,
      lat: pin.lat,
      lng: pin.lng,
      instructions,
      makeDefault,
    };
    try {
      onSaved(initial ? await updateAddress(initial.id, input) : await addAddress(input));
    } catch (caught) {
      setSaving(false);
      setError(caught instanceof ApiError ? caught.message : "We couldn't save your address. Please try again.");
    }
  };

  const fieldError = (bad: boolean, message: string) =>
    touched && bad ? (
      <p role="alert" className="mt-1 flex items-center gap-1 text-sm text-coral-600 dark:text-coral-300">
        <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
        {message}
      </p>
    ) : null;

  const summary = [house.trim(), street.trim()].filter(Boolean).join(", ");

  return (
    <form onSubmit={save} noValidate className="mx-auto flex max-w-xl flex-col gap-4 px-4 pb-44 pt-4">
      <div role="tablist" aria-label="How to set your location" className="grid grid-cols-2 gap-1 rounded-full bg-ocean-700/10 p-1 dark:bg-white/10">
        {(["pin", "search"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => setMode(value)}
            className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-full text-sm font-bold focus-visible:outline-2 focus-visible:outline-teal-600 ${
              mode === value ? "bg-ocean-800 text-white" : "text-ocean-800 dark:text-foam-100"
            }`}
          >
            {value === "pin" ? <MapPin className="h-4 w-4" aria-hidden="true" /> : <Search className="h-4 w-4" aria-hidden="true" />}
            {value === "pin" ? "Pin on map" : "Search place"}
          </button>
        ))}
      </div>

      {mode === "search" && (
        <div>
          <div role="search" className="flex gap-2">
            <label htmlFor="delivery-search" className="sr-only">Search for an area, landmark or pincode</label>
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-ocean-800/70" aria-hidden="true" />
              <input
                id="delivery-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // Enter searches; it must not submit the address form
                    void runSearch();
                  }
                }}
                placeholder="e.g. Carter Road, Bandra West, Mumbai"
                className={`${INPUT} pl-10 pr-10`}
              />
              {query && (
                <button type="button" aria-label="Clear search" onClick={() => { setQuery(""); setResults(null); }} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full">
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
            <button type="button" onClick={() => void runSearch()} disabled={searching || query.trim().length < 3} className="h-12 rounded-lg bg-ocean-800 px-4 text-sm font-bold text-white disabled:opacity-50">
              {searching ? <Loader2 className="h-5 w-5 animate-spin" aria-label="Searching" /> : "Search"}
            </button>
          </div>
          {results && results.length > 0 && (
            <ul aria-label="Search results" className="mt-2 divide-y divide-ocean-900/10 overflow-hidden rounded-xl bg-white shadow dark:divide-white/10 dark:bg-ocean-900">
              {results.map((place) => (
                <li key={`${place.lat},${place.lng}`}>
                  <button type="button" onClick={() => choosePlace(place)} className="flex w-full items-start gap-2 px-4 py-3 text-left text-sm hover:bg-ocean-700/5">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" aria-hidden="true" />
                    <span>{place.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div id="delivery-pin-area" tabIndex={-1} className="relative h-72 overflow-hidden rounded-2xl bg-ocean-700/10 shadow outline-none">
        <Suspense fallback={<p className="flex h-full items-center justify-center gap-2 text-sm"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />Loading map…</p>}>
          <PinMap liveZones={zones.enforced ? zones.zones : undefined} position={pin} onPick={(next) => { setAccuracy(null); void placePin(next); }} focusKey={focusKey} />
        </Suspense>
        {!pin && (
          <p className="pointer-events-none absolute left-1/2 top-3 z-[500] -translate-x-1/2 rounded-full bg-white/95 px-4 py-1.5 text-xs font-bold text-ocean-900 shadow">
            Tap the map to drop your delivery pin
          </p>
        )}
        <button
          type="button"
          onClick={locateMe}
          className="absolute inset-x-3 bottom-3 z-[500] flex min-h-12 items-center justify-between gap-2 rounded-xl bg-white px-4 text-sm font-bold text-ocean-950 shadow-lg focus-visible:outline-2 focus-visible:outline-teal-600"
        >
          <span className="flex items-center gap-2">
            {geoState === "locating" ? <Loader2 className="h-5 w-5 animate-spin text-teal-700" aria-hidden="true" /> : <Crosshair className="h-5 w-5 text-teal-700" aria-hidden="true" />}
            {geoState === "locating" ? "Finding you…" : "Use my current location"}
          </span>
          {accuracy !== null && <span className="rounded-full bg-ocean-700/10 px-2.5 py-0.5 text-xs text-ocean-800">Accurate to {accuracy} m</span>}
        </button>
      </div>
      <p className="-mt-2 text-xs text-ocean-800/75 dark:text-foam-100/70">
        Teal areas are where we deliver today; dashed areas are coming soon. Dots are our hubs.
      </p>
      <p role="status" className="min-h-5 text-sm text-ocean-800/85 dark:text-foam-100/80">
        {geoState === "looking-up" ? "Looking up the address…" : geoMessage}
      </p>
      {touched && problems.pin && (
        <p role="alert" className="-mt-2 flex items-center gap-1 text-sm text-coral-600 dark:text-coral-300">
          <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          Drop a pin on the map, search a place, or use your current location.
        </p>
      )}

      {pin && (
        <div className="flex items-start justify-between gap-3 rounded-xl bg-white p-4 shadow-sm dark:bg-ocean-900" aria-live="polite">
          <div className="flex min-w-0 items-start gap-3">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-display text-lg font-bold leading-tight">{zone?.zoneName ?? (city || "Selected location")}</p>
              <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">{[city, state].filter(Boolean).join(", ")}{pincode ? ` • ${pincode}` : ""}</p>
            </div>
          </div>
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${
              serviceable ? "bg-teal-100 text-teal-700" : "bg-coral-500/10 text-coral-600 dark:text-coral-300"
            }`}
          >
            {serviceable ? <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" /> : <CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />}
            {serviceable ? "We deliver here" : zone?.availability === "coming-soon" ? "Coming soon here" : "Not serviceable yet"}
          </span>
        </div>
      )}
      {pin && serviceable && zone?.typicalDeliveryWindow && (
        <p className="-mt-2 text-sm text-ocean-800/85 dark:text-foam-100/80">Typical delivery window in {zone.zoneName}: {zone.typicalDeliveryWindow}.</p>
      )}
      {pin && !serviceable && (
        <p className="-mt-2 text-sm text-ocean-800/85 dark:text-foam-100/80">
          {zones.enforced ? "You can save this address, but orders to it are declined until we deliver here (Terms, section 4.1)." : "You can still save this address. We may decline orders outside our serviceable areas (Terms, section 4.1)."}
        </p>
      )}

      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-ocean-900">
        <div>
          <label htmlFor="delivery-house" className="text-xs font-bold uppercase tracking-wide">House / flat / floor / tower <span className="text-coral-500" aria-hidden="true">*</span></label>
          <input id="delivery-house" value={house} maxLength={120} onChange={(e) => setHouse(e.target.value)} autoComplete="address-line1" placeholder="Flat 402, 4th Floor, Sea Breeze Oceanview" aria-invalid={touched && problems.house ? true : undefined} className={`${INPUT} mt-1.5`} />
          {fieldError(problems.house, "Enter your house or flat.")}
        </div>
        <div>
          <label htmlFor="delivery-street" className="text-xs font-bold uppercase tracking-wide">Building / street / road <span className="text-coral-500" aria-hidden="true">*</span></label>
          <input id="delivery-street" value={street} maxLength={160} onChange={(e) => setStreet(e.target.value)} autoComplete="address-line2" placeholder="Carter Road Promenade" aria-invalid={touched && problems.street ? true : undefined} className={`${INPUT} mt-1.5`} />
          {fieldError(problems.street, "Enter your street or building.")}
        </div>
        <div>
          <label htmlFor="delivery-landmark" className="text-xs font-bold uppercase tracking-wide">Nearby landmark <span className="font-normal normal-case text-ocean-800/70">(optional)</span></label>
          <input id="delivery-landmark" value={landmark} maxLength={120} onChange={(e) => setLandmark(e.target.value)} placeholder="Opposite the lighthouse" className={`${INPUT} mt-1.5`} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="delivery-city" className="text-xs font-bold uppercase tracking-wide">City *</label>
            <input id="delivery-city" value={city} maxLength={80} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" aria-invalid={touched && problems.city ? true : undefined} className={`${INPUT} mt-1.5`} />
            {fieldError(problems.city, "Enter your city.")}
          </div>
          <div>
            <label htmlFor="delivery-state" className="text-xs font-bold uppercase tracking-wide">State *</label>
            <input id="delivery-state" value={state} maxLength={60} onChange={(e) => setState(e.target.value)} autoComplete="address-level1" aria-invalid={touched && problems.state ? true : undefined} className={`${INPUT} mt-1.5`} />
            {fieldError(problems.state, "Enter your state.")}
          </div>
        </div>
        <div>
          <label htmlFor="delivery-pincode" className="text-xs font-bold uppercase tracking-wide">Pincode *</label>
          <input id="delivery-pincode" value={pincode} inputMode="numeric" maxLength={6} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))} autoComplete="postal-code" placeholder="400050" aria-invalid={touched && problems.pincode ? true : undefined} className={`${INPUT} mt-1.5`} />
          {fieldError(problems.pincode, "Enter a 6-digit pincode.")}
        </div>

        <fieldset>
          <legend className="text-xs font-bold uppercase tracking-wide">Save address as</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {TAGS.map(({ value, label: text, icon: Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={label === value}
                onClick={() => setLabel(value)}
                className={`inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-teal-600 ${
                  label === value ? "bg-ocean-800 text-white" : "bg-ocean-700/10 dark:bg-white/10"
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {text}
              </button>
            ))}
          </div>
        </fieldset>

        {!initial?.isDefault && (
          <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold">
            <input id="delivery-default" type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} className="h-5 w-5 accent-teal-700" />
            Make this my default delivery address
          </label>
        )}
      </div>

      <fieldset className="rounded-2xl bg-white p-4 shadow-sm dark:bg-ocean-900">
        <legend className="flex items-center gap-2 px-1 text-xs font-bold uppercase tracking-wide">
          <Snowflake className="h-4 w-4 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
          Delivery preferences (requests to the courier)
        </legend>
        <div className="mt-2 flex flex-col gap-2">
          {INSTRUCTIONS.map(({ key, title, body }) => (
            <label key={key} className="flex cursor-pointer items-start gap-3 rounded-xl bg-ocean-700/5 p-3 dark:bg-white/10">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">{title}</span>
                <span className="block text-sm text-ocean-800/85 dark:text-foam-100/80">{body}</span>
              </span>
              <input type="checkbox" checked={instructions[key]} onChange={(e) => setInstructions((c) => ({ ...c, [key]: e.target.checked }))} className="mt-0.5 h-6 w-6 shrink-0 accent-teal-700" />
            </label>
          ))}
        </div>
      </fieldset>

      <p className="text-center text-xs text-ocean-800/70 dark:text-foam-100/70">
        Map data © OpenStreetMap contributors. Your pin and address are stored on your ZenFresh account.
      </p>

      <div className="fixed inset-x-0 bottom-0 z-40 bg-foam-50/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] backdrop-blur-xl dark:bg-ocean-950/95">
        <div className="mx-auto flex max-w-xl flex-col gap-1.5">
          <p className="flex items-center gap-2 truncate text-sm font-semibold">
            <CircleCheck className="h-4 w-4 shrink-0 text-teal-700" aria-hidden="true" />
            <span className="truncate">{summary ? `Delivering to: ${summary}` : "Add your address to continue"}</span>
          </p>
          <p role="alert" className="text-sm text-coral-600 dark:text-coral-300">{error}</p>
          <button
            type="submit"
            aria-disabled={saving}
            className="flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-ocean-800 text-base font-bold text-white shadow-md hover:bg-ocean-700 aria-disabled:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          >
            {saving && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {saving ? "Saving…" : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}
